import {
  Tab as NovaBaseTab,
  TabProps as NovaTabProps,
  Tabs as NovaBaseTabs,
  TabsProps as NovaTabsProps,
} from '@fme-nova/components'
import { default as MuiTabBase, TabProps as MuiTabProps } from '@mui/material/Tab'
import { default as MuiTabsBase, TabsProps as MuiTabsProps } from '@mui/material/Tabs'
import React, { ReactNode, useState } from 'react'

import { css, styled, uiTokens } from '../../theme'
import { createComponentWrapperWithRef, THEME_CONST } from '../../theme'
import { COLORS } from '../../theme/colors'
import { Divider } from '../Divider'

const MuiTab = styled(MuiTabBase)`
  color: ${COLORS.text.secondary};
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
  border-bottom: 2px solid transparent;
  /* stylelint-disable shorthand-property-no-redundant-values */
  padding: 9px 16px 7px 16px;
  min-height: 42px;

  &:hover {
    color: ${COLORS.primary.main};
    background-color: ${COLORS.primary.hover};
    border-bottom: 2px solid ${COLORS.primary.main};
  }
  &.Mui-selected {
    color: ${COLORS.primary.main};
  }
`

const MuiStyledTabs = styled(MuiTabsBase)`
  min-height: ${THEME_CONST.tabMinHeight}px;
  max-height: ${THEME_CONST.tabMaxHeight}px;
  padding-left: ${THEME_CONST.defaultPadding}px;
  padding-right: ${THEME_CONST.defaultPadding}px;

  background-color: ${COLORS.white.main};
  .MuiTabs-indicator {
    background-color: ${COLORS.primary.main};
  }
`

const NovaStyledTabs = styled(NovaBaseTabs)`
  ${(props) =>
    props.orientation !== 'vertical' &&
    css`
      padding-left: ${uiTokens.spacing.padding.large}px;
      padding-right: ${uiTokens.spacing.padding.large}px;

      background-color: ${uiTokens.color.basic.white};
    `}
`

const MuiStyledTabContent = styled('div')`
  padding-top: ${THEME_CONST.defaultPadding}px;
  background-color: ${COLORS.components.tab.content.background};
`

const NovaStyledTabContent = styled('div')`
  background-color: ${uiTokens.uiSurface.background};
`

const StyledTabContent = createComponentWrapperWithRef(
  {
    mui: MuiStyledTabContent,
    nova: NovaStyledTabContent,
  },
  'TabContent',
)

export interface TabEntity {
  label: string | ReactNode
  content: React.ReactNode
  isCustom?: boolean
}

interface TabContentProps {
  children?: React.ReactNode
  index: number
  value: number
  isCustom?: boolean
}

function TabContent(props: TabContentProps) {
  const { children, value, index, isCustom, ...other } = props

  const classNames = ['tab-content']
  if (isCustom) {
    classNames.push('tab-content--custom')
  }
  if (value === index) {
    classNames.push('tab-content--active')
  }

  return (
    <StyledTabContent
      className={classNames.join(' ')}
      data-test-id="tab-content"
      role="tabpanel"
      hidden={value !== index}
      id={`tab-content-${index}`}
      aria-labelledby={`tab-content-${index}`}
      {...other}
    >
      {value === index && children}
    </StyledTabContent>
  )
}

export const Tab = createComponentWrapperWithRef<MuiTabProps | NovaTabProps>(
  {
    mui: MuiTab,
    nova: NovaBaseTab,
  },
  'Tab',
)

const TabsComponent = createComponentWrapperWithRef<MuiTabsProps | NovaTabsProps>(
  {
    mui: MuiStyledTabs,
    nova: NovaStyledTabs,
  },
  'TabsComponent',
)

export function Tabs(props: {
  childs: TabEntity[]
  onChange?: (newValue: number) => void
  startingTab?: number
  orientation?: 'horizontal' | 'vertical'
}) {
  const [selected, setSelected] = useState(props.startingTab ?? 0)

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setSelected(newValue)
    if (props.onChange) {
      props.onChange(newValue)
    }
  }

  return (
    <>
      <TabsComponent data-test-id="tabs" value={selected} onChange={handleChange} orientation={props.orientation}>
        {props.childs.map((e, idx) => {
          return <Tab disableRipple data-test-id="tab-item" key={idx} label={e.label}></Tab>
        })}
      </TabsComponent>
      {props.orientation !== 'vertical' && <Divider />}
      {props.childs.map((e, idx) => {
        return (
          <TabContent key={idx} index={idx} value={selected} isCustom={e.isCustom}>
            {e.content}
          </TabContent>
        )
      })}
    </>
  )
}
